import { put, select, all } from "redux-saga/effects";
import { cloneDeep } from "lodash";

import makeRequest from "rdx/utils/makeRequest";
import getErrorActions from "rdx/utils/getErrorActions";
import selectors from "rdx/modules/projects/selectors";
import leadsSelectors from "rdx/modules/leads/selectors";
import platformSelectors from "rdx/modules/platform/selectors";
import preSalesSelectors from "rdx/modules/projectPreSales/selectors";
import { orgProjectsApi } from "rdx/modules/enterpriseProjects/endpoints";
import { ORG_PROJECTS_TAGS } from "rdx/modules/enterpriseProjects/tags";
import actions from "rdx/actions";
import { keysToSnakeCase, camelizeKeys } from "lib/helpers";

import messages from "../messages";

function* updateLead(action) {
  const { leadId } = action.payload;

  if (!leadId) {
    throw new Error(`Invalid lead ID = ${leadId} specified. Please check this parameter.`);
  }

  const params = keysToSnakeCase(action.payload);
  const { success, data, error } = yield* makeRequest.patch(`/leads/${leadId}`, params);

  if (success && data) {
    const leadDetails = camelizeKeys(data.body);
    const { siteInfo, providerLeads } = leadDetails;
    const project = yield select(selectors.getProjectDetails);
    const existingLeadDetails = yield select(leadsSelectors.getLeadDetails);
    const l2pDrawerLeadId = yield select(platformSelectors.selectLeadToProposalDrawerLeadId);
    const l2pDrawerProjectDetails = yield select(platformSelectors.selectLeadToProposalProjectDetails);

    const updatedSiteInfoDetails = {
      utilityBillUrl: siteInfo.utilityBillUrl,
      kwhConsumption: siteInfo.kwhConsumption,
      utilityCompany: siteInfo.utilityCompany,
      roofAge: siteInfo.roofAge,
      roofType: siteInfo.roofType,
      servicePanel: siteInfo.servicePanel,
      mounting: siteInfo.mounting,
      otherDetails: siteInfo.otherDetails,
      hoaName: siteInfo.hoaName,
      hoaPhone: siteInfo.hoaPhone,
      financing: siteInfo.financing,
      workType: siteInfo.workType,
    };

    const constructionDetails = {
      constructionDetails: providerLeads[0].constructionDetails,
    };
    const updatedProviderLead = {
      ...project,
      providerLead: {
        ...project.providerLead,
        ...updatedSiteInfoDetails,
        ...constructionDetails,
        lead: leadDetails,
      },
      lead: leadDetails,
    };

    const { projects: presalesProjects, paging: presalesPaging } = yield select(preSalesSelectors.getProjectPreSales);
    const idxOfUpdatedLead = presalesProjects.findIndex((proj) => proj.leadId === parseInt(leadId, 10));
    const updatedPreSales = cloneDeep(presalesProjects);
    updatedPreSales[idxOfUpdatedLead] = {
      ...updatedPreSales[idxOfUpdatedLead],
      leadFirstName: leadDetails.firstName,
      leadLastName: leadDetails.lastName,
      leadEmail: leadDetails.email,
      leadPhone: leadDetails.phone,
      addressCity: leadDetails.address.city,
      addressState: leadDetails.address.state,
      coalescedUpdatedAt: leadDetails.updatedAt,
    };

    yield all([
      ...(l2pDrawerLeadId === leadId
        ? [
            put(actions.setLeadToProposalDrawerLeadDetails(leadDetails)),
            put(
              actions.setLeadToProposalDrawerProjectDetails({
                ...l2pDrawerProjectDetails,
                ...updatedSiteInfoDetails,
                ...constructionDetails,
              }),
            ),
          ]
        : []),
      ...(existingLeadDetails?.id === leadId ? [put(actions.setLeadDetails(leadDetails))] : []),
      ...(project?.lead?.id === leadId ? [put(actions.setProjectDetails(updatedProviderLead))] : []),
      put(actions.newSuccessEvent({ message: messages.LEAD_UPDATE_SUCCESS })),
      put(
        actions.setProjectsPreSales({
          projects: updatedPreSales,
          paging: presalesPaging,
        }),
      ),
      put(orgProjectsApi.util.invalidateTags([ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS])),
    ]);
  } else {
    if (error.message.includes("Do Not Call")) {
      return getErrorActions({ error, message: messages.ERROR_UPDATING_LEAD_DNC_PHONE });
    }
    return getErrorActions({ error, message: messages.ERROR_UPDATING_LEAD });
  }
  return null;
}

export default updateLead;
